import { useMemo } from 'react';

import { ItemCheckResult } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { convertCheckResultToProposal } from 'entities/Order/lib/convertCheckResultToProposal/convertCheckResultToProposal';
import { isOrderSubmitted } from 'entities/Order/lib/isOrderSubmitted/isOrderSubmitted';
import { OrderStatuses } from 'entities/Order/model/consts/orderStatuses';
import {
    Proposal,
    SelectedProposal,
} from 'entities/Proposal/model/types/ProposalSchema';
import { Vendor } from 'entities/Vendors';

interface UseUnselectedProposalsProps {
    isReOpsUser: boolean;
    orderStatus?: OrderStatuses | string;
    allCheckResults?: ItemCheckResult[];
    selectedProposals?: SelectedProposal[];
    proposals?: Proposal[];
    vendorsMap?: Record<string, Vendor>;
}

export const useUnselectedProposals = ({
    isReOpsUser,
    orderStatus,
    allCheckResults,
    selectedProposals,
    proposals,
    vendorsMap,
}: UseUnselectedProposalsProps) => {
    return useMemo(() => {
        if (
            !isReOpsUser ||
            !isOrderSubmitted(orderStatus) ||
            !allCheckResults ||
            allCheckResults.length === 0
        ) {
            return [];
        }

        // Get invUids and sellerUids from selected proposals to identify which were selected
        // We match by both invUid (more precise) and sellerUid (fallback)
        const selectedInvUids = new Set(
            selectedProposals
                ?.map((sp) => sp.uid)
                .filter((uid): uid is string => Boolean(uid)) || [],
        );
        const selectedSellerUids = new Set(
            selectedProposals
                ?.map((sp) => sp.sellerUid)
                .filter((uid): uid is string => Boolean(uid)) || [],
        );

        // Also check proposals for invId matches
        const proposalInvIds = new Set(
            proposals
                ?.map((p) => p.invId)
                .filter((id): id is string => Boolean(id)) || [],
        );

        // Find check results that weren't selected
        // A check result is unselected if:
        // 1. Its invUid doesn't match any selected proposal uid or proposal invId
        // 2. Its sellerUid doesn't match any selected proposal sellerUid (fallback)
        // 3. It has a valid price
        const unselectedCheckResults = allCheckResults.filter(
            (checkResult) =>
                checkResult.sellPrice > 0 && // Only include valid prices
                checkResult.invUid &&
                !selectedInvUids.has(checkResult.invUid) &&
                !proposalInvIds.has(checkResult.invUid) &&
                (!checkResult.sellerUid ||
                    !selectedSellerUids.has(checkResult.sellerUid)),
        );

        // Convert unselected check results to HydratedProposal format
        return unselectedCheckResults.map((checkResult) =>
            convertCheckResultToProposal(checkResult, vendorsMap),
        );
    }, [
        isReOpsUser,
        orderStatus,
        allCheckResults,
        selectedProposals,
        proposals,
        vendorsMap,
    ]);
};
